import React, { useEffect } from 'react';
import { Images, useModal } from 'utils';
import { generationPhases, useFormAiGeneration } from '../hooks/useFormAiGeneration';

/**
 * Values double as the row's modifier class (`ai-generation-row done`).
 *
 * Two states, not three. The passes run in parallel and finish in whatever order
 * the model and the rate limiter allow, so there is no "the one being worked on"
 * to point at — an earlier version put a spinner on the first unfinished row and
 * it sat there spinning while rows further down ticked green, which reads as a
 * stall rather than as progress. The bar and the count carry the liveness; the
 * rows carry the outcome.
 */
const rowState = { DONE: 'done', PENDING: 'pending' };

/** Left-hand caption under the bar — says what is happening, not just how far along. */
const phaseLabel = (phase, completed, total) => {
    if (phase === generationPhases.PENDING || !total) return 'Reading file...';
    if (phase === generationPhases.COMPLETED) return `${total} section${total === 1 ? '' : 's'} added`;
    return `${completed} section${completed === 1 ? '' : 's'} added`;
};

const SectionRow = ({ name, done }) => (
    <div className={`ai-generation-row ${done ? rowState.DONE : rowState.PENDING}`}>
        <span className="ai-generation-row-status">
            {done ? <img src={Images.checkTickGreen} alt="" /> : <span className="ai-generation-row-status-inner" />}
        </span>
        <p className="ai-generation-row-name">{name}</p>
    </div>
);

export const AiGenerationModal = ({ formId, conversationId, onSection, onComplete }) => {
    const { close } = useModal();

    const { phase, sections, completedIds, completed, total, percent, error } = useFormAiGeneration({
        formId,
        conversationId,
        enabled: true,
        onSection,
        onDone: onComplete,
    });

    const failed = phase === generationPhases.FAILED;
    const finished = phase === generationPhases.COMPLETED;

    useEffect(() => {
        if (!finished) return undefined;
        const timer = setTimeout(close, 900);
        return () => clearTimeout(timer);
    }, [finished]);

    return (
        <div className="ai-generation-modal">

            <div className="ai-generation-header">
                <div className={`ai-generation-icon ${failed ? 'failed' : ''}`}>
                    {/* A failure is not a quieter version of progress — it gets the
                        alert mark, not the document mark in a grey ring. */}
                    <img src={failed ? Images.alertError : Images.documentTextBlue} alt="" />
                </div>
                <p className="ai-generation-title">{failed ? 'Generation failed' : 'Generating your template...'}</p>
                <p className="ai-generation-subtitle">
                    {failed ? error : "We're building the template from your uploaded file and instructions."}
                </p>
            </div>

            {!failed && (
                <div className="ai-generation-progress">
                    <progress className="ai-generation-progress-bar" value={percent} max={100} />
                    <div className="ai-generation-progress-caption">
                        <p>{phaseLabel(phase, completed, total)}</p>
                        <p className="ai-generation-progress-percent">{percent}%</p>
                    </div>
                </div>
            )}

            {!!sections.length && (
                // `running` drives the pending rows' pulse. It stops on failure
                // and on completion, where a breathing dot would claim work that
                // is no longer happening.
                <div className={`ai-generation-rows ${!failed && !finished ? 'running' : ''}`}>
                    {sections.map((section) => (
                        <SectionRow key={section.id} name={section.name} done={completedIds.includes(section.id)} />
                    ))}
                </div>
            )}

            {failed && (
                // `close-btn-box` is the app's secondary button. Secondary on
                // purpose: the template exists and is editable, so this dismisses
                // a notice rather than performing the primary action.
                <button type="button" className="close-btn-box ai-generation-fallback-btn" onClick={close}>
                    Edit template manually
                </button>
            )}
        </div>
    );
};
